/* eslint-disable no-underscore-dangle */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import BusinessIcon from '@mui/icons-material/Business';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

import Icon from '@shared/atoms/Icon';
import IconButton from '@shared/atoms/IconButton';
import FullWidthTabs from '@shared/atoms/FullWidthTabs';
import T from '@shared/atoms/Typography';
import Dialog from '@shared/molecules/Dialog';
import Menu from '@shared/molecules/Menu';
import theme from '@shared/themes/default';

import FeaturedItemTab from './tabs/FeaturedItemTab';
import SettingsTab from './tabs/SettingsTab';
import SubscriptionTab from './tabs/SubscriptionTab';
import { updateOrganization } from '../graphql/mutations';
import { useAppGlobals } from '../../../contexts/AppContext';

const OuterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: ${theme.spacing.m1} ${theme.spacing.m1} 0 ${theme.spacing.m1};
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.s};
`;

const LeftTitleWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  gap: ${theme.spacing.s};
`;

const OrganizationsTableRowItem = ({
  item,
  onUpdateOrganization,
  setErrorMessage,
  setDetailedErrors,
  setHasError,
  setHasSuccess,
  setSuccessMessage,
}) => {
  const { t } = useTranslation();
  const { useGraphQLMutation } = useAppGlobals();

  const [anchorEl, setAnchorEl] = useState(null);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [allowArchive, setAllowArchive] = useState(
    t('backoffice.organizations.archive.wantToArchive', {
      name: item.name,
    })
  );
  const [hasSubscription, setHasSubscription] = useState(false);

  const customMenuHandler = (event) => {
    setAnchorEl(anchorEl === null ? event.currentTarget : null);
  };

  useEffect(() => {
    if (item.__typename === 'Organization') {
      if (item.subscription) {
        setAllowArchive(
          t('backoffice.organizations.archive.cantArchive', {
            name: item.name,
          })
        );
        setHasSubscription(true);
      } else {
        setAllowArchive(
          t('backoffice.organizations.archive.wantToArchive', {
            name: item.name,
          })
        );
        setHasSubscription(false);
      }
    }
  }, [item]);

  const [updateOrganizationMutation, updateOrganizationMutationState] =
    useGraphQLMutation(updateOrganization, {
      update(cache, { data: { updateOrganization } }) {
        onUpdateOrganization(cache, updateOrganization);
        setSuccessMessage(
          updateOrganization.archived
            ? t('backoffice.organizations.archive.successfullyArchived', {
                name: updateOrganization.name,
              })
            : t('backoffice.organizations.archive.successfullyUnarchived', {
                name: updateOrganization.name,
              })
        );
        setHasSuccess(true);
        setArchiveDialogOpen(false);
      },
      onError() {
        setErrorMessage(
          item.archived
            ? t('backoffice.organizations.archive.failedToUnarchive', {
                name: item.name,
              })
            : t('backoffice.organizations.archive.failedToArchive', {
                name: item.name,
              })
        );
        setHasError(true);
        setArchiveDialogOpen(false);
      },
    });

  return (
    <>
      <OuterWrapper>
        <TitleWrapper>
          <LeftTitleWrapper>
            <Icon icon={BusinessIcon} />
            <T fontWeight="semibold" variant="subtitle">
              {item.name}
            </T>
          </LeftTitleWrapper>
          <IconButton
            ariaLabel={t('generic.more')}
            icon={MoreHorizIcon}
            onClick={customMenuHandler}
            theme="dark"
            type="primary"
          />
          <Menu
            freeMenu
            initAnchorEl={anchorEl}
            menuItems={[
              {
                label: item.archived
                  ? t('backoffice.organizations.archive.unarchive')
                  : t('backoffice.organizations.archive.archive'),
                handler: () => setArchiveDialogOpen(true),
              },
            ]}
            setInitAnchorEl={setAnchorEl}
            theme="dark"
          />
        </TitleWrapper>
        <FullWidthTabs
          onChangeTabHandler={(index) => setSelectedTab(index)}
          selectedTabIndex={selectedTab}
          tabsToSelect={[
            {
              label: t('backoffice.organizations.subscription.title'),
              component: (
                <SubscriptionTab
                  item={item}
                  onUpdateOrganization={onUpdateOrganization}
                  setErrorMessage={setErrorMessage}
                  setHasError={setHasError}
                  setHasSuccess={setHasSuccess}
                  setSuccessMessage={setSuccessMessage}
                />
              ),
            },
            {
              label: t('backoffice.organizations.settings.title'),
              component: (
                <SettingsTab
                  item={item}
                  onUpdateOrganization={onUpdateOrganization}
                  setErrorMessage={setErrorMessage}
                  setDetailedErrors={setDetailedErrors}
                  setHasError={setHasError}
                  setHasSuccess={setHasSuccess}
                  setSuccessMessage={setSuccessMessage}
                />
              ),
            },
            {
              label: t('backoffice.organizations.featuredItem.tabTitle'),
              component: (
                <FeaturedItemTab
                  item={item}
                  setErrorMessage={setErrorMessage}
                  setHasError={setHasError}
                  setHasSuccess={setHasSuccess}
                  setSuccessMessage={setSuccessMessage}
                />
              ),
            },
          ]}
          theme="dark"
        />
      </OuterWrapper>
      <Dialog
        ariaLabel={
          item.archived
            ? t('backoffice.organizations.archive.unarchive')
            : t('backoffice.organizations.archive.archive')
        }
        actions={[
          {
            label: t('generic.cancel'),
            handler: () => setArchiveDialogOpen(false),
            type: 'secondaryOutline',
          },
          {
            label: t('generic.confirm'),
            disabled: hasSubscription,
            handler: () =>
              updateOrganizationMutation({
                variables: {
                  input: {
                    id: item.id,
                    archived: !item.archived,
                  },
                },
              }),
          },
        ]}
        loading={updateOrganizationMutationState.loading}
        onClose={() => setArchiveDialogOpen(false)}
        open={archiveDialogOpen}
        title={
          item.archived
            ? t('backoffice.organizations.archive.unarchive')
            : t('backoffice.organizations.archive.archive')
        }
      >
        <T color={theme.palette.mainDark}>
          {item.archived
            ? t('backoffice.organizations.archive.wantToUnarchive', {
                name: item.name,
              })
            : allowArchive}
        </T>
      </Dialog>
    </>
  );
};

OrganizationsTableRowItem.propTypes = {
  item: PropTypes.object.isRequired,
  onUpdateOrganization: PropTypes.func.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
  setDetailedErrors: PropTypes.func.isRequired,
  setHasError: PropTypes.func.isRequired,
  setHasSuccess: PropTypes.func.isRequired,
  setSuccessMessage: PropTypes.func.isRequired,
};

export default OrganizationsTableRowItem;
